import { useRouter } from 'next/router';
import { AttributeIconArea } from '@common/AttributeIconArea/AttributeIconArea';
import { Badge } from '@common/Badge/Badge';
import { Button, TYPE } from '@common/Button/Button';
import { Modal, ModalVariant } from '@common/Modal/Modal';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { useIAConfigurator } from '@hooks/useIAConfigurator';
import { iaConfigEventHandler, useIAConfiguratorEvents } from '@hooks/useIAConfiguratorEvents';
import { AssociationType } from '@libs/associations';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { getLocale, type DeliveryTime } from '@libs/constants';
import {
  getAssociationProductReferences,
  getProductDescription,
  getProductIdForLocale,
  getProductTitle,
} from '@libs/product.helper';
import { TECHNICAL_ECHAIN_ICON_ATTRIBUTES } from '@libs/technical-data';
import { ProductHelper } from '@models/graphql';
import type { EChainBuyBoxProps } from '@product/BuyBox/EChain/EChainBuyBox';
import { DecisionToolsBox, LifetimeCalculatorModes } from '@product/DecisionToolsBox/DecisionToolsBox';
import { EChainExpertTeaserBox } from '@product/EChainExpertTeaserBox/EChainExpertTeaserBox';
import {
  ProductMediaCarousel,
  ProductMediaCarouselDisplayType,
} from '@product/ProductMediaCarousel/ProductMediaCarousel';
import { useShelvesProductAssociationModelsQuery } from '@product/ProductPage/PageLayouts/hooks/useShelvesProductAssociationModelsQuery';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { ProductTabs } from '@product/ProductTabs/ProductTabs';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { SharedProductPageProps } from '../ProductPage';
import { useMountingBracketsSelection } from './hooks/useMountingBracketsSelection';
import { useSupportTraysSelection } from './hooks/useSupportTraysSelection';

export type EChainProductPageLayoutProps = SharedProductPageProps;

export const renderBuyBoxLoadPlaceholder = () => <div className="bg-gray-50 min-h-[70rem]" />;

const BuyBoxWithNoSSR = dynamic<EChainBuyBoxProps>(
  () => import('@product/BuyBox/EChain/EChainBuyBox').then(mod => mod.EChainBuyBox),
  {
    ssr: false,
    loading: renderBuyBoxLoadPlaceholder,
  }
);

export const EChainProductPageLayout: React.FC<EChainProductPageLayoutProps> = ({ carouselImages }) => {
  const { t } = useTranslation(['product/productDetails', 'product/echainBuyBox']);

  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const isProductDiscontinued = ProductHelper.isDiscontinued(currentProduct);
  const isProductNew = ProductHelper.isNew(currentProduct);
  const ketteEffektiveLaenge = useEchainProductStore(state => state.kette.EffektiveLaenge?.Value || 0);
  const ketteVerfahrweg = useEchainProductStore(state => state.kette.Verfahrweg?.Value);
  const ketteNumGlieder = useEchainProductStore(state => state.kette.NumGlieder);
  const einbauArt = useEchainProductStore(state => state.einbauArt);
  const unitSystem = useProductPageStore(state => state.unitSystem);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  const { data: shelvesProductAssociationModelsResult, isLoading: isLoadingShelvesProductAssociationModels } =
    useShelvesProductAssociationModelsQuery();

  const shelvesProductAssociationModels = useMemo(
    () => (!isLoadingShelvesProductAssociationModels ? shelvesProductAssociationModelsResult : []),
    [isLoadingShelvesProductAssociationModels, shelvesProductAssociationModelsResult]
  );

  const unfilteredSeparatorsAssociationProducts = useMemo(
    () =>
      !isLoadingUniqueAssociatedProducts && uniqueAssociatedProducts
        ? getAssociationProductReferences(currentProduct, [AssociationType.SEPARATORS])
            ?.map(associationReference => uniqueAssociatedProducts[associationReference.id])
            .filter(product => product) || []
        : [],
    [currentProduct, isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts]
  );

  const { hasMountingBracketSelectionsChanged, resetHasMountingBracketSelectionsChanged } =
    useMountingBracketsSelection();
  const { isProductSupportTraysAvailable, hasSupportTraySelectionsChanged, resetHasSupportTraySelectionsChanged } =
    useSupportTraysSelection();

  const { isIaConfiguratorWindowOpen, openIaConfiguratorWindow } = useIAConfigurator(
    currentProduct.id,
    ketteEffektiveLaenge
  );
  const { hasProductVariantSelectionChanged, resetHasProductVariantSelectionChanged } = useIAConfiguratorEvents(
    currentProduct.id,
    ketteEffektiveLaenge,
    unfilteredSeparatorsAssociationProducts,
    shelvesProductAssociationModels,
    iaConfigEventHandler
  );

  const closeModal = useCallback(() => {
    resetHasSupportTraySelectionsChanged();
    resetHasMountingBracketSelectionsChanged();
    resetHasProductVariantSelectionChanged();
  }, [
    resetHasSupportTraySelectionsChanged,
    resetHasMountingBracketSelectionsChanged,
    resetHasProductVariantSelectionChanged,
  ]);

  useEffect(() => {
    if (isIaConfiguratorWindowOpen) {
      closeModal();
    }
  }, [isIaConfiguratorWindowOpen, closeModal]);

  useEffect(() => {
    if (hasProductVariantSelectionChanged && !hasMountingBracketSelectionsChanged && !hasSupportTraySelectionsChanged) {
      resetHasProductVariantSelectionChanged();
    }
  }, [
    hasProductVariantSelectionChanged,
    hasMountingBracketSelectionsChanged,
    hasSupportTraySelectionsChanged,
    resetHasProductVariantSelectionChanged,
  ]);

  let lifetimeCalculatorMode: LifetimeCalculatorModes = LifetimeCalculatorModes.NONE;
  if (
    getAttributeValue<boolean>(
      currentProduct.attributes?.release_of_service_life_calculation_ESL_bool as ProductAttribute<boolean>
    )
  ) {
    lifetimeCalculatorMode = LifetimeCalculatorModes.ECHAIN;
  }

  return (
    <>
      <h1 className="my-10 leading-5xl">{getProductTitle(currentProduct)}</h1>

      <div data-testid="page-layout-echain" className="main-content-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-8 justify-items-center igus-text-styles grid-rows-[auto,_1fr]">
          <div className="lg:col-span-3 w-auto sm:w-full order-1">
            {isProductNew && <Badge text={t('PRODUCT_DETAILS.NEW_PRODUCT_BADGE')} />}
            {isProductDiscontinued && <Badge text={t('PRODUCT_DETAILS.DISCONTINUED_BADGE')} />}
            {carouselImages?.length > 0 && (
              <ProductMediaCarousel media={carouselImages} displayType={ProductMediaCarouselDisplayType.FullWidth} />
            )}
          </div>
          <div className="lg:col-span-3 w-full order-2 md:order-3 lg:order-2">
            <AttributeIconArea
              unitSystem={unitSystem}
              currentProduct={currentProduct}
              attributeIcons={TECHNICAL_ECHAIN_ICON_ATTRIBUTES}
            />
          </div>
          <div className="md:row-span-2 lg:col-span-2 relative h-full w-full order-3 md:order-2 lg:order-3">
            <div className="sticky top-8 z-30">
              <BuyBoxWithNoSSR
                sku={getProductIdForLocale(currentProduct, locale)}
                deliveryTimeCode={
                  currentProduct.delivery_times
                    ? (currentProduct.delivery_times[0]?.code?.toString() as DeliveryTime)
                    : undefined
                }
                deliveryTimeText={
                  currentProduct.delivery_times && currentProduct.delivery_times[0]?.label
                    ? currentProduct.delivery_times[0]?.label
                    : undefined
                }
              />
              <DecisionToolsBox
                showExpertSystem={false}
                showEplan={false}
                showSampleBox={false}
                lifetimeCalculatorMode={lifetimeCalculatorMode}
                showZuken={false}
                showCAD={getAttributeValue<boolean>(
                  currentProduct.attributes?.cad_data_bool as ProductAttribute<boolean>
                )}
                showLifeTimeBearingMode={false}
                showRoleConfigurator={false}
                showLeadScrewConfigurator={false}
                variantSku={currentProduct.id}
                einbauArt={einbauArt}
                description={getProductTitle(currentProduct)}
                disabled={ProductHelper.isDiscontinued(currentProduct)}
                verfahrweg={ketteVerfahrweg}
                effektiveLaenge={ketteEffektiveLaenge}
                numGlieder={ketteNumGlieder}
              />
              <EChainExpertTeaserBox />
            </div>
          </div>
          <div className="md:col-span-2 lg:col-span-6 w-full -mt-8 -mb-8 order-4">
            <ProductTabs
              productDetails={getProductDescription(currentProduct)}
              suitableProducts={null}
              showOnlineTools={false}
            />
          </div>
        </div>
      </div>

      <Modal
        variant={ModalVariant.ALERT}
        visible={
          hasProductVariantSelectionChanged && (hasMountingBracketSelectionsChanged || hasSupportTraySelectionsChanged)
        }
        onClose={closeModal}
        headline={t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.TITLE', { ns: 'product/echainBuyBox' })}
      >
        <p className="mb-8 text-base">
          {t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.DESCRIPTION', { ns: 'product/echainBuyBox' })}
        </p>

        {hasMountingBracketSelectionsChanged && (
          <p
            className="mb-8 text-base"
            dangerouslySetInnerHTML={{
              __html: t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.DESCRIPTION_MOUNTING_BRACKETS_CHANGED', {
                ns: 'product/echainBuyBox',
              }),
            }}
          />
        )}
        {hasSupportTraySelectionsChanged &&
          (isProductSupportTraysAvailable ? (
            <p
              className="mb-8 text-base"
              dangerouslySetInnerHTML={{
                __html: t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.DESCRIPTION_SUPPORT_TRAYS_CHANGED', {
                  ns: 'product/echainBuyBox',
                }),
              }}
            />
          ) : (
            <p
              className="mb-8 text-base"
              dangerouslySetInnerHTML={{
                __html: t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.DESCRIPTION_SUPPORT_TRAYS_NOT_AVAILABLE', {
                  ns: 'product/echainBuyBox',
                }),
              }}
            />
          ))}

        <div className="flex justify-between gap-3.5">
          <Button
            text={t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.CONFIRMATION_BUTTON', { ns: 'product/echainBuyBox' })}
            className="flex-1"
            onClick={closeModal}
          />
          <Button
            className="flex-1"
            type={TYPE.SECONDARY}
            text={t('ECHAIN_PRODUCT_PAGE.USER_HINT_MODAL.EDIT_CONFIGURATION_BUTTON', { ns: 'product/echainBuyBox' })}
            onClick={() => openIaConfiguratorWindow(true)}
            testId="goto-shopping-cart"
          />
        </div>
      </Modal>
    </>
  );
};
